import { useEffect, useState } from "react";
import { X, Heart } from "lucide-react";
import { Avatar } from "@/components/Avatar";
import type { Liker } from "@/lib/feedApi";

interface Props {
  title?: string;
  load: () => Promise<{ users: Liker[] }>;
  onClose: () => void;
}

export function LikersDialog({ title = "Liked by", load, onClose }: Props) {
  const [users, setUsers] = useState<Liker[] | null>(null);

  useEffect(() => {
    let alive = true;
    load()
      .then((r) => alive && setUsers(r.users))
      .catch(() => alive && setUsers([]));
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      alive = false;
      window.removeEventListener("keydown", onKey);
    };
  }, [load, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-24" role="dialog" aria-modal="true">
      <button type="button" aria-label="Close" onClick={onClose} className="fixed inset-0 bg-overlay backdrop-blur-[2px]" />
      <div className="relative w-full max-w-sm rounded-[var(--radius-lg)] border border-border-subtle bg-surface shadow-[var(--shadow-4)]">
        <div className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-fg [&_svg]:size-4 [&_svg]:text-accent-text">
            <Heart /> {title}
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="flex size-7 items-center justify-center rounded-[var(--radius-sm)] text-fg-muted hover:bg-surface-hover hover:text-fg [&_svg]:size-4"
          >
            <X />
          </button>
        </div>
        <div className="max-h-80 overflow-auto p-2">
          {users === null ? (
            <p className="px-2 py-6 text-center text-sm text-fg-muted">Loading…</p>
          ) : users.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-fg-muted">No likes yet.</p>
          ) : (
            <ul className="flex flex-col">
              {users.map((u, i) => (
                <li key={i} className="flex items-center gap-2.5 rounded-[var(--radius-md)] px-2 py-2">
                  <Avatar name={u.name} src={u.avatarUrl} size="sm" />
                  <span className="truncate text-sm text-fg">{u.name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
